import React from "react";
import { Trans } from "@lingui/react/macro";
import { EventSponsor } from "~/graphql/codegen/graphql";
import metrics from "~/utils/metrics";
import { sendSponsorTapBeacon } from "~/components/community/sponsor-tap-beacon";
import { buildInterestUrl } from "~/components/community/get-involved-urls";

interface SponsorGroupProps {
  heading: React.ReactNode;
  sponsors: EventSponsor[];
  metricEventName: "tapped_event_partner" | "tapped_event_sponsor";
  eventId: string;
  communityAdminUrl: string;
}

function SponsorGroup({
  heading,
  sponsors,
  metricEventName,
  eventId,
  communityAdminUrl,
}: SponsorGroupProps) {
  return (
    <div className="flex flex-col space-y-2">
      <div className="text-base font-bold">{heading}</div>
      <div className="grid gap-3 sm:grid-cols-2">
        {sponsors.map((eventSponsor) => {
          const description = eventSponsor.sponsor.description?.trim();
          const handleSponsorClick = () => {
            metrics.track(metricEventName, {
              ...eventSponsor.sponsor,
            });
            // The direct href is authoritative; attribution must never affect it.
            try {
              sendSponsorTapBeacon({
                communityAdminUrl,
                sponsorId: eventSponsor.sponsor.id,
                eventId,
                surface: "event_page",
              });
            } catch {
              // Best-effort analytics only.
            }
          };

          return (
            <div
              className="bg-muted rounded-xl p-3"
              key={eventSponsor.sponsor.id}
            >
              <a
                href={eventSponsor.sponsor.url}
                target="_blank"
                rel="noreferrer"
                onClick={handleSponsorClick}
                className="flex items-start gap-3"
              >
                <div className="bg-background/70 rounded-lg h-12 w-20 shrink-0 p-2">
                  <img
                    className="object-contain h-full w-full"
                    src={eventSponsor.sponsor.logourl}
                    alt={eventSponsor.sponsor.name}
                  />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold leading-tight">
                    {eventSponsor.sponsor.name}
                  </div>
                  {description ? (
                    <div className="mt-1 text-xs text-muted-foreground leading-snug">
                      {description}
                    </div>
                  ) : null}
                </div>
              </a>
              <a
                href={eventSponsor.sponsor.url}
                target="_blank"
                rel="noreferrer"
                onClick={handleSponsorClick}
                className="mt-2 inline-flex text-xs font-medium text-primary hover:underline"
              >
                <Trans>Visit website</Trans>
              </a>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function MindspaceEventSponsors({
  sponsors,
  eventId,
  communityAdminUrl,
}: {
  sponsors: EventSponsor[] | undefined;
  eventId: string;
  communityAdminUrl: string;
}) {
  if (!sponsors || sponsors.length === 0) {
    return null;
  }

  const partners = sponsors.filter((s) => s.isPartner);
  const regularSponsors = sponsors.filter((s) => !s.isPartner);

  return (
    <div className="flex flex-col space-y-6 min-w-[300px] py-6">
      {partners.length > 0 && (
        <SponsorGroup
          heading={<Trans>Event Partners</Trans>}
          sponsors={partners}
          metricEventName="tapped_event_partner"
          eventId={eventId}
          communityAdminUrl={communityAdminUrl}
        />
      )}

      {regularSponsors.length > 0 && (
        <SponsorGroup
          heading={<Trans>Event Sponsors</Trans>}
          sponsors={regularSponsors}
          metricEventName="tapped_event_sponsor"
          eventId={eventId}
          communityAdminUrl={communityAdminUrl}
        />
      )}
      <p className="text-sm text-muted-foreground">
        <Trans>Want to see your brand here?</Trans>{" "}
        <a
          href={buildInterestUrl(
            communityAdminUrl,
            "event_page",
            "sponsor-signup-card",
            eventId,
          )}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline"
        >
          <Trans>Become a sponsor</Trans> →
        </a>
      </p>
    </div>
  );
}
